// Metric-strip derivation for the Home overview: fold the three live sources
// (`/v1/overview/counts`, `/v1/overview/burn`, `/v1/fleet/utilization`) into
// the four display-ready tiles the canvas APP artboard draws. Pure over its
// inputs, so unit-testable without a query.
//
// NO PLACEHOLDER METRICS. A tile whose source has not loaded, or was REFUSED
// (a token without `read:sessions` gets a 403 on the burn and still sees the
// counts), resolves to `UNSET` — never a zero, which would be a claim. The
// container passes `undefined` for both cases; this module cannot tell them
// apart and does not need to.

import { t, tPlural } from '../i18n';
import type { UtilBox } from './fleetUtilization';
import { type ApprovalRow, type LiveWork, oldestApprovalIso } from './overview';
import type { OverviewBurn, OverviewCounts } from './overview-counts';
import { money, moneyFromDecimal, since } from './time';
import { UNSET } from './unset';

/** The tile's drill-in target, resolved to an org href by `StatBand`. */
export type StatTileId = 'merged' | 'open_prs' | 'boxes' | 'burn';

/** One strip cell; `sub` is omitted, never blank, when the tile has none. */
export type StatTile = {
  readonly id: StatTileId;
  readonly label: string;
  readonly value: string;
  readonly sub?: string;
  readonly tone: 'neutral' | 'live' | 'shipped';
};

export type StatTileSources = {
  readonly counts: OverviewCounts | undefined;
  readonly burn: OverviewBurn | undefined;
  /** The utilization roster; undefined until it loads or when it was refused. */
  readonly boxes: readonly UtilBox[] | undefined;
  /**
   * The account's sessions in flight right now (`inFlightCount` over the sessions the
   * Home page already holds), undefined when that read has not answered. These are
   * worker-side runs (reviews, triage, babysit) that hold NO box, so the boxes tile can
   * read 0/N while the account is busy; this sub-line is what makes that visible.
   */
  readonly liveWork?: LiveWork | undefined;
};

/** A box is WORKING when it is online and holds at least one session. */
export function workingBoxes(boxes: readonly UtilBox[]): number {
  return boxes.filter((b) => b.online && b.active_sessions > 0).length;
}

/**
 * "1/3", or the dash when ANY box's presence went unread: the api then sends every box
 * `online: false, active_sessions: 0`, and a "0/N" there would claim a measured idle fleet.
 */
function boxesValue(boxes: readonly UtilBox[]): string {
  if (boxes.some((b) => b.presence === 'unknown')) return UNSET;
  return t('overview.boxes_value', { working: workingBoxes(boxes), total: boxes.length });
}

/**
 * The boxes tile's live-work line. A complete count is a measurement, its zero included.
 * A partial window's count is a floor ("4+"), and its zero claims nothing: no line.
 */
function liveWorkSub(work: LiveWork): string | undefined {
  const n = work.count;
  if (!work.complete) return n > 0 ? t('overview.boxes_sub_live_floor', { count: n }) : undefined;
  if (n === 0) return t('overview.boxes_sub_none');
  return tPlural('overview.boxes_sub_live', n, { count: n });
}

/**
 * A count, ungrouped. `lib/time.ts` has no number seam yet and a private
 * `toLocaleString` is exactly what `testing/formatter-seam.test.ts` ratchets out.
 */
const count = (n: number): string => String(n);

/**
 * The hour's burn. A null `cost_usd` is "no priced session in the window": with
 * no sessions at all that is an honest $0.00, but when every session in it is
 * unpriced the spend is UNKNOWN, and a $0.00 there would understate a real bill.
 */
function burnValue(burn: OverviewBurn): string {
  if (burn.cost_usd !== null) return moneyFromDecimal(burn.cost_usd);
  return burn.sessions === 0 ? money(0) : UNSET;
}

/** `sub` is omitted, never blank (`StatTile`). */
const withSub = (sub: string | undefined): { sub?: string } => (sub === undefined ? {} : { sub });

export function statTiles(input: StatTileSources): readonly StatTile[] {
  const dash = UNSET;
  const { counts, burn, boxes, liveWork } = input;
  return [
    {
      id: 'merged',
      label: t('overview.merged_24h'),
      value: counts ? count(counts.merged_24h) : dash,
      tone: 'shipped',
    },
    {
      id: 'open_prs',
      label: t('overview.open_prs'),
      value: counts ? count(counts.open_prs) : dash,
      ...(counts
        ? {
            sub: tPlural('overview.open_prs_sub', counts.repos_enabled, {
              count: counts.repos_enabled,
            }),
          }
        : {}),
      tone: 'neutral',
    },
    {
      id: 'boxes',
      label: t('overview.boxes_working'),
      value: boxes ? boxesValue(boxes) : dash,
      ...withSub(liveWork === undefined ? undefined : liveWorkSub(liveWork)),
      tone: 'live',
    },
    {
      id: 'burn',
      label: t('overview.burn_hr'),
      value: burn ? burnValue(burn) : dash,
      ...(burn && burn.unpriced > 0
        ? { sub: t('overview.burn_unpriced', { count: burn.unpriced }) }
        : {}),
      tone: 'neutral',
    },
  ];
}

/**
 * The pending queue's head line — "2 drafted · oldest 51d" — over the rows the
 * table renders (stream-merged, so it moves with them). Null when nothing waits:
 * the empty state says that, and a "0 drafted" beside it would say it twice.
 */
export function pendingSummary(rows: readonly ApprovalRow[]): string | null {
  const oldest = oldestApprovalIso(rows);
  if (oldest === null) return null;
  return tPlural('pending.summary', rows.length, { count: rows.length, age: since(oldest) });
}
